import { CheckIcon, ChevronDownIcon, SearchIcon } from 'lucide-react';
import { Popover as PopoverPrimitive } from 'radix-ui';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/cn';
import { fold } from '@/shared/lib/fold';
import { controlSurface } from '@/shared/ui/input';

export type ComboboxOption = { value: string; label: string };

type ComboboxProps = Omit<
  React.ComponentProps<'button'>,
  'value' | 'onChange' | 'children' | 'role' | 'type'
> & {
  options: readonly ComboboxOption[];
  value?: string | undefined;
  onValueChange?: (value: string) => void;
  /** Shown on the trigger while there is no value. */
  placeholder?: string;
  /** Accessible name and placeholder of the search field. Defaults to `common:combobox.search`. */
  searchLabel?: string;
  /** Shown when the search matches nothing. Defaults to `common:combobox.empty`. */
  emptyText?: string;
  /** `compact` is the workspace density. */
  size?: 'default' | 'compact';
};

/**
 * A select for long lists: a button that opens a search field over a listbox. Focus stays in the search
 * field while the arrow keys move the active option (`aria-activedescendant`); Enter picks, Escape closes.
 * Name it with a label (`FormLabel`, `Field`) or `aria-label`, like any other control.
 */
function Combobox({
  options,
  value,
  onValueChange,
  placeholder,
  searchLabel,
  emptyText,
  size = 'default',
  className,
  disabled,
  ...props
}: ComboboxProps) {
  const { t } = useTranslation('common');
  const listId = React.useId();
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [active, setActive] = React.useState(0);
  const listRef = React.useRef<HTMLDivElement>(null);

  const filtered = React.useMemo(() => {
    const needle = fold(query.trim());
    return needle ? options.filter((o) => fold(o.label).includes(needle)) : options;
  }, [options, query]);
  const selected = options.find((o) => o.value === value);
  const optionId = (index: number) => `${listId}-option-${index}`;

  function onOpenChange(next: boolean) {
    setOpen(next);
    if (next) {
      setQuery('');
      setActive(
        Math.max(
          0,
          options.findIndex((o) => o.value === value),
        ),
      );
    }
  }

  function pick(option: ComboboxOption | undefined) {
    if (!option) return;
    onValueChange?.(option.value);
    setOpen(false);
  }

  // Keep the active option in view; jsdom has no scrollIntoView.
  React.useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector(`[data-index="${active}"]`)
      ?.scrollIntoView?.({ block: 'nearest' });
  }, [open, active]);

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    const last = filtered.length - 1;
    const moves: Record<string, number> = {
      ArrowDown: Math.min(active + 1, last),
      ArrowUp: Math.max(active - 1, 0),
      Home: 0,
      End: last,
    };
    if (event.key in moves) {
      event.preventDefault();
      setActive(Math.max(0, moves[event.key] ?? 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      pick(filtered[active]);
    }
  }

  const search = searchLabel ?? t('combobox.search');
  return (
    <PopoverPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-haspopup="listbox"
          data-slot="combobox-trigger"
          data-size={size}
          data-placeholder={selected ? undefined : ''}
          disabled={disabled}
          className={cn(
            controlSurface,
            'flex w-full cursor-pointer items-center justify-between gap-2 px-3 text-left whitespace-nowrap data-[placeholder]:text-muted-foreground data-[size=compact]:h-8 data-[size=compact]:text-sm data-[size=default]:h-10 data-[size=default]:text-base',
            className,
          )}
          {...props}
        >
          <span className="truncate">{selected?.label ?? placeholder}</span>
          <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          data-slot="combobox-content"
          aria-label={search}
          align="start"
          sideOffset={4}
          className="z-50 flex max-h-(--radix-popover-content-available-height) w-(--radix-popover-trigger-width) min-w-[12rem] origin-(--radix-popover-content-transform-origin) flex-col overflow-hidden rounded-sm border bg-popover text-popover-foreground shadow-overlay data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95"
        >
          <div className="flex items-center gap-2 border-b px-3 focus-within:shadow-[inset_0_-2px_0_var(--ring)]">
            <SearchIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <input
              type="search"
              aria-label={search}
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={filtered.length > 0 ? optionId(active) : undefined}
              placeholder={search}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setActive(0);
              }}
              onKeyDown={onKeyDown}
              className={cn(
                'h-10 w-full min-w-0 appearance-none bg-transparent outline-none placeholder:text-muted-foreground [&::-webkit-search-cancel-button]:appearance-none',
                size === 'compact' ? 'text-sm' : 'text-base',
              )}
            />
          </div>
          <div
            ref={listRef}
            id={listId}
            role="listbox"
            aria-label={search}
            className="max-h-64 overflow-y-auto p-1"
          >
            {filtered.map((option, index) => (
              // biome-ignore lint/a11y/useKeyWithClickEvents: keys are handled on the search field, which keeps focus (aria-activedescendant pattern)
              <div
                key={option.value}
                id={optionId(index)}
                role="option"
                tabIndex={-1}
                aria-selected={option.value === value}
                data-index={index}
                data-active={index === active ? '' : undefined}
                onClick={() => pick(option)}
                onPointerMove={() => setActive(index)}
                className={cn(
                  'flex cursor-pointer items-center justify-between gap-2 rounded-xs px-2 py-1.5 select-none data-[active]:bg-accent data-[active]:text-accent-foreground',
                  size === 'compact' ? 'text-sm' : 'text-base',
                )}
              >
                <span className="truncate">{option.label}</span>
                {option.value === value ? (
                  <CheckIcon className="size-4 shrink-0" aria-hidden="true" />
                ) : null}
              </div>
            ))}
          </div>
          {filtered.length === 0 ? (
            <p className="px-3 py-4 text-center text-sm text-muted-foreground">
              {emptyText ?? t('combobox.empty')}
            </p>
          ) : null}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

export { Combobox };
